import '../styles/pages.css';
import { store } from '../state';
import { i18n } from '../i18n';
import { pagesRegistry } from '../pages';
import type { AppState } from '../types';
import { renderTemplate } from '../template-engine';
//import { buildTemplateData, formatters } from '../template-utils';
import { buildTemplateData } from '../template-utils';
import pageCardTpl from '../templates/page-card.html?raw';

const els = {
  pagesListContainer: document.getElementById('pages-list-container') as HTMLElement,
  pageContentContainer: document.getElementById('page-content-container') as HTMLElement,
};

// ============================================================================
// Инициализация обработчиков событий
// ============================================================================

export function initEventListeners(): void {
  // Делегирование событий для списка страниц
  els.pagesListContainer.addEventListener('click', (e) => {
    const item = ((e.target as HTMLElement).closest('.pages-list-item')) as HTMLElement | null;
    if (item?.dataset?.id) {
      const id = item.dataset.id;
      const page = pagesRegistry.getById(id);
      if (page) {
        store.dispatch({ type: 'SELECT_PAGE', payload: page });
        loadPageContent(id);
      }
    }
  });
}

// ============================================================================
// Загрузка содержимого страницы
// ============================================================================

async function loadPageContent(pageId: string): Promise<void> {
  const state = store.getState();

  // Если уже загружено для этой страницы — не перезагружаем
  if (
    state.selectedPage?.id === pageId &&
    state.pageContent &&
    !state.pageError
  ) {
    return;
  }

  store.dispatch({ type: 'SET_PAGE_LOADING', payload: true });
  store.dispatch({ type: 'SET_PAGE_ERROR', payload: null });

  try {
    const content = await pagesRegistry.loadContent(pageId);
    console.log('content is ', content);
    store.dispatch({ type: 'SET_PAGE_CONTENT', payload: content });
  } catch (error) {
    console.error('Ошибка загрузки страницы:', error);
    store.dispatch({
      type: 'SET_PAGE_ERROR',
      payload: i18n.t('pages.error.load') || 'Не удалось загрузить страницу'
    });
  } finally {
    store.dispatch({ type: 'SET_PAGE_LOADING', payload: false });
  }
}

// ============================================================================
// Загрузка списка страниц
// ============================================================================

export function loadPages(): void {
  const state = store.getState();

  // Если уже загружено — не перезагружаем
  if (state.pagesList.length > 0 || state.pagesLoading) return;

  store.dispatch({ type: 'SET_PAGES_LOADING', payload: true });

  try {
    // Инициализируем registry (если ещё не инициализирован)
    pagesRegistry.init();

    const pages = pagesRegistry.getAll();
    store.dispatch({ type: 'SET_PAGES_LIST', payload: pages });

    // АВТОВЫБОР: если ничего не выбрано, выбираем первый элемент
    if (!state.selectedPage && pages.length > 0) {
      const firstPage = pages[0];
      store.dispatch({ type: 'SELECT_PAGE', payload: firstPage });
      loadPageContent(firstPage.id);
    }
  } catch (error) {
    console.error('Ошибка загрузки списка страниц:', error);
    store.dispatch({
      type: 'SET_PAGES_ERROR',
      payload: i18n.t('pages.error.load_list') || 'Не удалось загрузить список страниц'
    });
  } finally {
    store.dispatch({ type: 'SET_PAGES_LOADING', payload: false });
  }
}

// ============================================================================
// Рендеринг
// ============================================================================

export function renderPages(state: AppState): void {
  console.log('🎨 renderPages() вызвана');
  console.log('📊 selectedPage:', state.selectedPage);
  console.log('📊 pageContent:', state.pageContent ? `${state.pageContent.length} символов` : 'null');
  console.log('📊 pageLoading:', state.pageLoading);
  console.log('📊 pageError:', state.pageError);
    
  // 1. Рендер списка (левая колонка)
  if (state.pagesLoading) {
    els.pagesListContainer.innerHTML = `
      <div class="state-view loading" style="min-height: 150px;">
        <div class="spinner" style="width: 30px; height: 30px;"></div>
      </div>
    `;
  } else if (state.pagesError) {
    els.pagesListContainer.innerHTML = `
      <div class="state-view error">
        <p>${escapeHtml(state.pagesError)}</p>
      </div>
    `;
  } else if (state.pagesList.length === 0) {
    els.pagesListContainer.innerHTML = `
      <div class="state-view empty">
        <p data-i18n="pages.empty_list">Нет доступных страниц</p>
      </div>
    `;
  } else {
    els.pagesListContainer.innerHTML = state.pagesList.map(page => {
      const isActive = state.selectedPage?.id === page.id;
      return `
        <div class="pages-list-item ${isActive ? 'active' : ''}" data-id="${page.id}">
          ${page.icon ? `<span class="pages-list-icon">${page.icon}</span>` : ''}
          <div class="item-title">${escapeHtml(page.title)}</div>
        </div>
      `;
    }).join('');
  }

  // 2. Рендер содержимого (правая колонка)
  if (state.pageLoading) {
    els.pageContentContainer.innerHTML = `
      <div class="state-view loading">
        <div class="spinner" style="width: 40px; height: 40px;"></div>
        <p>Загрузка содержимого...</p>
      </div>
    `;
  } else if (state.pageError) {
    els.pageContentContainer.innerHTML = `
      <div class="state-view error">
        <h3>Ошибка загрузки</h3>
        <p>${escapeHtml(state.pageError)}</p>
      </div>
    `;
  } else if (!state.selectedPage) {
    els.pageContentContainer.innerHTML = `
      <div class="stub-content">
        <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" style="color: var(--accent-color); margin-bottom: 16px;">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
          <polyline points="14 2 14 8 20 8"></polyline>
          <line x1="16" y1="13" x2="8" y2="13"></line>
          <line x1="16" y1="17" x2="8" y2="17"></line>
          <polyline points="10 9 9 9 8 9"></polyline>
        </svg>
        <h2 data-i18n="pages.select_prompt">Выберите страницу</h2>
        <p data-i18n="pages.select_prompt_desc">для просмотра содержимого</p>
      </div>
    `;
  } else {
    // Используем шаблон для рендеринга карточки страницы
    const page = state.selectedPage;
    const templateData = buildTemplateData(
      page,
      {
        icon: { key: 'icon', fallback: '' },
//        icon: { key: 'icon', fallback: '📄' },
        title: { key: 'title' },
//        contentHtml: { key: '__content', format: formatters.html, fallback: '' },
      },
      {
        contentHtml: state.pageContent || '',
        i18n: {
          pages: {
            title: i18n.t('pages.title') || 'Страница'
          }
        }
      }
    );
    console.log('📦 templateData:', templateData);
    console.log('📄 Шаблон:', pageCardTpl);
    const renderedHtml = renderTemplate(pageCardTpl, templateData);
    console.log('🎭 Отрендеренный HTML (первые 500 символов):', renderedHtml.substring(0, 500));
    
    els.pageContentContainer.innerHTML = renderedHtml;
    
    console.log('🔍 pageContentContainer.innerHTML после установки:', 
    els.pageContentContainer.innerHTML.substring(0, 500));

    // Обработка ссылок внутри содержимого — открываем в новой вкладке
    els.pageContentContainer.querySelectorAll('a').forEach((link) => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        const href = (link as HTMLAnchorElement).href;
        window.open(href, '_blank');
      });
    });
  }
}

// ============================================================================
// Вспомогательные функции
// ============================================================================

const escapeHtml = (str: string | undefined | null): string => {
  if (str == null) return '';
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
};